import { useEffect } from "react";

import { useLocation, useNavigate } from "react-router-dom";

import { useAppDispatch, useTypedSelector } from "@mightybot/core";

import { isAuthRoute } from "../components/Auth/authUtils";

import type { RootState } from "@mightybot/core";

const DEFAULT_LOGIN_PATH = "/login";
const SIGNUP_PATH = "/signup";

/**
 * Watches the auth slice for `pendingNotice` writes (dispatched by the API
 * layer on session-end) and performs the soft navigation that used to live
 * inside `base.ts` as `window.location.href = …`. The hard reload was the
 * root cause of the "sticky" auth banner: it forced a localStorage round-trip
 * to survive the page reload, and every cycle re-mounted Auth and re-read
 * the flag. Now navigation is a router transition; the queued notice lives
 * in Redux until `useAuthController` consumes it.
 *
 * Behavior:
 * - `pendingNotice === null` → nothing to do.
 * - already on an auth route → no navigation; the auth screen reads the
 *   notice directly from the store (in-place display).
 * - on a protected route → soft-navigate to `/signup` if the current path
 *   looks like a signup flow, else `/login`, preserving the intended
 *   destination via React Router `location.state` (not localStorage).
 */
export function useAuthRouterObserver(): void {
	const dispatch = useAppDispatch();
	const navigate = useNavigate();
	const location = useLocation();
	const pendingNotice = useTypedSelector(
		(state: RootState) => state.authState.pendingNotice,
	);

	useEffect(() => {
		if (pendingNotice === null) return;
		if (isAuthRoute()) return; // Auth screen reads from store; no nav needed.

		const target = location.pathname.includes(SIGNUP_PATH)
			? SIGNUP_PATH
			: DEFAULT_LOGIN_PATH;
		const fromPath = `${location.pathname}${location.search}`;
		navigate(`${target}?redirect=${encodeURIComponent(fromPath)}`, {
			replace: true,
			state: { intendedDestination: fromPath },
		});
		// `pendingNotice` is consumed by useAuthController on the auth-screen
		// mount; we leave it set here so the navigation target actually sees it.
	}, [pendingNotice, location.pathname, location.search, navigate, dispatch]);
}
